import { Head, Link, usePage } from "@inertiajs/react";
import { LandingNav, LandingFooter } from "@/components/landing/LandingChrome";
import FlashStack from "@/components/layout/FlashStack";
import RsvpForm from "@/components/events/RsvpForm";
import Panel from "@/components/ui/Panel";
import EmptyState from "@/components/ui/EmptyState";
import { ButtonLink } from "@/components/ui/Button";
import type { PublicProps } from "@/types";
import type { SatelliteEvent, EventRsvp } from "@/interfaces/satellite_event";

interface Prefill {
  name: string | null;
  email: string | null;
  birth_month: number | null;
  birth_year: number | null;
}

// One city's signup form, on its own screen. The city page is a pitch several
// screens long, so somebody who has already decided gets a short page with the
// form at the top of it rather than a scroll back through the sales copy.
//
// It posts to the same endpoint the platform has always used, so capacity, the
// age window, the rate limit and the disposable-domain check are all still the
// server's call - this page only decides what to show.
export default function EventSignup({
  event,
  my_rsvp,
  rsvps_open,
  age_range,
  heard_about_options,
  prefill,
}: {
  event: SatelliteEvent;
  my_rsvp: EventRsvp | null;
  rsvps_open: boolean;
  age_range: { min: number; max: number };
  heard_about_options: string[];
  prefill: Prefill;
}) {
  const { props } = usePage<PublicProps>();
  const program = props.program;
  const cityPath = `/events/${event.slug ?? event.id}`;
  const going = my_rsvp != null && my_rsvp.aasm_state !== "cancelled";

  // Every reason there might be no form to show, in the order that matters to
  // somebody standing here: already in, too late, closed, full.
  const blocked = going
    ? {
        title: "You're already on the list",
        body: "Check your email for the confirmation link. You can cancel your spot from the event page.",
      }
    : event.past
      ? {
          title: "This one already happened",
          body: "Have a look at the other cities - there may still be one near you.",
        }
      : !rsvps_open
        ? {
            title: "Sign-ups aren't open yet",
            body: "This city is confirmed, the form just isn't open yet. Check back in a few days.",
          }
        : event.full
          ? {
              title: "This one is full",
              body: "Every spot is taken. Other cities may still have room.",
            }
          : null;

  return (
    <>
      <Head title={`Sign up - ${program.name} ${event.city ?? event.name}`} />

      <FlashStack />

      <div className="text-ink min-h-screen bg-white antialiased">
        <LandingNav signedIn={props.user != null} signUpHref={cityPath} />

        <div className="bg-band-hero border-line border-b-2 px-4 py-10 sm:px-6">
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-2">
            <Link
              href={cityPath}
              className="text-caption text-ink-muted font-bold hover:underline"
            >
              &lt; back to {event.city ?? event.name}
            </Link>
            <h1 className="font-display text-title text-ink font-bold">
              Sign up for {program.name} {event.city ?? event.name}
            </h1>
            <p className="text-body text-ink-muted">
              {event.date_label
                ? `${event.date_label}${event.time_range_label ? `, ${event.time_range_label}` : ""}. `
                : ""}
              It's free, and you don't need an account.
            </p>
          </div>
        </div>

        <div className="bg-surface px-4 py-10 sm:px-6">
          <div className="mx-auto w-full max-w-3xl">
            {blocked ? (
              <Panel title="rsvp.form">
                <EmptyState title={blocked.title} description={blocked.body} />
                <div className="mt-4 flex flex-wrap justify-center gap-3">
                  <ButtonLink href={cityPath} variant="secondary">
                    Back to {event.city ?? event.name}
                  </ButtonLink>
                  <ButtonLink href="/events">Browse every city</ButtonLink>
                </div>
              </Panel>
            ) : (
              <RsvpForm
                event={event}
                ageRange={age_range}
                heardAboutOptions={heard_about_options}
                prefill={prefill}
              />
            )}
          </div>
        </div>

        <LandingFooter />
      </div>
    </>
  );
}
